import {
  CLAIM_STATUS,
  INCOME_CERTIFICATE,
  VICTIM,
} from 'containers/data-structure';
import moment from 'moment';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import currentIntervenantStorage from 'containers/services/security/current-intervenant-storage';
import currentUserStorage from 'containers/services/security/current-user-storage';
import { get, isEmpty, isNil } from 'lodash';
import {
  STATUS_PENDING,
  STATUS_REJECTED,
  STATUS_VALIDATED,
} from 'containers/LawyerFeeNote/constants';
import { EXPERTISE_ACCOUNTING_ID } from 'containers/CourtManagerLegalProceedings/constants';
import { getTheLatestEvaluationObject } from 'containers/LawyerLegalProceedingsPage/judgementAdd/IncomeCertificate/utils';
import { CURRENT_JUDICIARY_STEP_EXPERT_REPORT } from 'containers/CourtManagerLegalProceedings/ExpertReportAppeal/constants';
import { getVictimAge } from 'containers/CourtManagerLegalProceedings/ExpertReport/utils';
import { MODE } from 'containers/CourtManagerLegalProceedings/ExpertReport/constants';
import { VictimType } from 'typings/globalTypes';

export const formatEvaluation = (
  victim: VictimType,
  dahirAmount: any,
  victims: VictimType[],
  claim: any,
  certificate: any,
) => {
  const hasValidatedAccountinExpertise = !isEmpty(
    get(victim, 'victimExpertise', []).find(
      (element: any) =>
        ![STATUS_PENDING, STATUS_REJECTED].includes(element.status) &&
        element.expertiseId === EXPERTISE_ACCOUNTING_ID,
    ),
  );
  const lastEvaluation = getTheLatestEvaluationObject(
    get(victim, 'victimKey'),
    dahirAmount,
  );
  if (
    !isEmpty(victim) &&
    !hasValidatedAccountinExpertise &&
    !isEmpty(lastEvaluation)
  ) {
    return {
      gestionMode: '',
      materialAmount: '',
      currentJudiciaryStep: CURRENT_JUDICIARY_STEP_EXPERT_REPORT,
      judiciaryAmount: '',
      responsibilityRate: String(lastEvaluation.responsibilityRate),
      age: getVictimAge(
        get(victim, 'id'),
        victims,
        get(claim, 'claim.lossDate'),
      ),
      rateRecovery: String(lastEvaluation.rateRecovery),
      revenu: parseInt(get(certificate, 'income')),
      mode: get(victim, 'injuryPostKey') === '2' ? MODE.DECEASED : MODE.BLESSE,
      ratePretium: String(lastEvaluation.ratePretium),
      rateAestheticDamageWithoutConsequence: String(
        lastEvaluation.rateAestheticDamageWithoutConsequence,
      ),
      lossDate: moment(get(claim, 'claim.lossDate')).toISOString(true),
      rateAestheticDamageWithConsequence: String(
        lastEvaluation.rateAestheticDamageWithConsequence,
      ),
      rateCareerPrejudice: String(lastEvaluation.rateCareerPrejudice),
      rateSchoolingPrejudice: String(lastEvaluation.rateSchoolingPrejudice),
      rateIPP: String(lastEvaluation.rateIPP),
      rateITT: String(lastEvaluation.rateITT),
      victimKey: get(victim, 'victimKey'),
      medicalPharmaceuticalFees: String(
        lastEvaluation.medicalPharmaceuticalFees,
      ),
      rateMedicalPharmaceuticalFees: String(
        lastEvaluation.rateMedicalPharmaceuticalFees,
      ),
      transactionPhase: 1,
      JudiciartDecisionDate: '',
      expType: '',
      isRecentExpertiseVictim: false,
    };
  } else if (
    !isEmpty(victim) &&
    !hasValidatedAccountinExpertise &&
    isEmpty(lastEvaluation)
  ) {
    return {
      gestionMode: '',
      materialAmount: '',
      currentJudiciaryStep: CURRENT_JUDICIARY_STEP_EXPERT_REPORT,
      judiciaryAmount: '',
      responsibilityRate: String(get(claim, 'claim.responsibilityShare', null)),
      rateRecovery: '',
      revenu: String(get(certificate, 'income')),
      age: getVictimAge(
        get(victim, 'id'),
        victims,
        get(claim, 'claim.lossDate'),
      ),
      mode: get(victim, 'injuryPostKey') === '2' ? MODE.DECEASED : MODE.BLESSE,
      ratePretium: '',
      rateAestheticDamageWithoutConsequence: '',
      rateAestheticDamageWithConsequence: '',
      rateCareerPrejudice: '',
      rateSchoolingPrejudice: '',
      rateIPP: '',
      lossDate: moment(get(claim, 'claim.lossDate')).toISOString(true),
      rateITT: get(victim, 'itt'),
      victimKey: get(victim, 'victimKey'),
      medicalPharmaceuticalFees: '',
      rateMedicalPharmaceuticalFees: '',
      transactionPhase: 1,
      JudiciartDecisionDate: '',
      expType: '',
      isRecentExpertiseVictim: false,
    };
  } else {
    return {};
  }
};

export const formatIncomePayloadToUpdate = (
  certificate: any,
  claimNumber: string,
  claimId: number,
  judiciaryProcessId: any,
  victim: VictimType,
  isManager: any,
  dahirAmount: any,
  victims: any,
  claim: any,
  riskKey: string,
) => {
  const commonPayload = {
    ...certificate,
    claimNumber: claimNumber,
    claimId: claimId,
    judiciaryProcessId: judiciaryProcessId,
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
    toUpdateVictim:
      Number(get(victim, VICTIM.ANNUAL_INCOME)) !==
      Number(get(certificate, INCOME_CERTIFICATE.INCOME)),
    isLawyer: !isManager,
    status:
      !isManager &&
      (get(certificate, 'status') === CLAIM_STATUS.REJECTED ||
        isNil(get(certificate, 'status', null)))
        ? CLAIM_STATUS.PENDING
        : get(certificate, 'status'),
    updatedBy: !isManager
      ? currentIntervenantStorage?.get().preferred_username
      : currentUserStorage?.get().username,
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
  };
  if (get(certificate, 'status') === STATUS_VALIDATED && isManager) {
    const sagisEvaluations = formatEvaluation(
      victim,
      dahirAmount,
      victims,
      claim,
      certificate,
    ) as any;
    return {
      ...commonPayload,
      sagisEvaluations: !isEmpty(sagisEvaluations) ? [sagisEvaluations] : [],
      provisionBody:
        !isEmpty(sagisEvaluations) && riskKey
          ? {
              riskID: riskKey,
              date: moment().toISOString(true),
            }
          : {},
    };
  } else {
    return commonPayload;
  }
};

export const formatPayloadToMakeDecision = (
  certificate: any,
  status: string,
  riskKey: string,
  claimId: number,
  judiciaryProcessId: any,
  isManager: any,
  victim: VictimType,
  dahirAmount: any,
  claim: any,
  victims: any,
  reason: any,
) => {
  const commonPayload = {
    certificate: {
      ...certificate,
      rejectionReason: reason,
      decidedBy: currentUserStorage.get().username,
      decidedAt: moment().format(),
      status: status,
    },
    decidedBy: currentUserStorage.get().username,
    claimId: claimId,
    claimNumber: get(claim, 'claim.claimNumber'),
    judiciaryProcessId: judiciaryProcessId,
    isLawyer: !isManager,
    isTrackingActive: featureService.hasFeature(FEATURES.TRACKING_ACTIONS_USER),
    isInboxesActive: featureService.hasFeature(
      FEATURES.ATTRIBUTION_INBOXES_MANAGER,
    ),
  };
  if (status === STATUS_REJECTED) {
    return {
      ...commonPayload,
      sagisEvaluations: [],
      provisionBody: {},
    };
  } else {
    const sagisEvaluations = formatEvaluation(
      victim,
      dahirAmount,
      victims,
      claim,
      certificate,
    ) as any;
    return {
      ...commonPayload,
      sagisEvaluations: !isEmpty(sagisEvaluations) ? [sagisEvaluations] : [],
      provisionBody:
        !isEmpty(sagisEvaluations) && riskKey
          ? {
              riskID: riskKey,
              date: moment().toISOString(true),
            }
          : {},
    };
  }
};
